import { useRef, useState } from 'react';
import { lerNumero, metro, type AvancoSpt, type CamadaSpt, type FenomenoSpt, type MetroSpt } from '../../api/spt';
import { HACHURAS, padraoDaHachura } from './hachuras';
import { arrumarTextos, baseArrastada, naArrastado, pontosDoGrafico, TETO_DO_GRAFICO } from './perfil';

/**
 * O perfil do furo SPT — onda 357 (spec 12 §5; RN-276, D2, D7). Ao lado da
 * tabela, na mesma escala: a base de cada camada, a gênese, a hachura, a
 * descrição (clicável), o gráfico (N' tracejado, N cheio, Critério 1), o NA
 * e o avanço. O «+» cria camada na profundidade; a linha da base se arrasta
 * de 5 em 5 cm; o ▼ do NA também.
 */

/* As colunas: a da descrição e a do gráfico acompanham a largura da página (361: sem rolagem para o lado). Primeiro
   a descrição encolhe até o mínimo; depois o gráfico, até 130 px. */
const FIXAS = { camada: 50, genese: 34, hachura: 56, na: 40, avanco: 44 };
const SOMA_FIXA = Object.values(FIXAS).reduce((a, b) => a + b, 0);
const DESCRICAO = { min: 150, max: 520 };
const GRAFICO = { min: 130, max: 220 };
function colunas(disponivel: number) {
  const grafico = Math.max(GRAFICO.min, Math.min(GRAFICO.max, disponivel - SOMA_FIXA - DESCRICAO.min));
  const descricao = Math.max(DESCRICAO.min, Math.min(DESCRICAO.max, disponivel - SOMA_FIXA - grafico));
  const COL = { ...FIXAS, descricao, grafico };
  const X = {
    genese: COL.camada,
    hachura: COL.camada + COL.genese,
    descricao: COL.camada + COL.genese + COL.hachura,
    grafico: COL.camada + COL.genese + COL.hachura + COL.descricao,
    na: COL.camada + COL.genese + COL.hachura + COL.descricao + COL.grafico,
    avanco: COL.camada + COL.genese + COL.hachura + COL.descricao + COL.grafico + COL.na,
  };
  return { COL, X, largura: X.avanco + COL.avanco };
}

type Arrasto = { tipo: 'base'; id: string; min: number; max: number; prof: number } | { tipo: 'na'; prof: number };

export function PerfilDoFuro({
  metros, camadas, avanco, fenomenos, fim, na, rh, selecionada, editavel, largura: disponivel, geneses,
  aoEscolher, aoDividir, aoMudarBase, aoMudarFim, aoMudarNa, aoMudarGenese, aoExcluir,
}: {
  metros: MetroSpt[]; camadas: CamadaSpt[]; avanco: AvancoSpt[]; fenomenos: FenomenoSpt[];
  fim: number | null; na: number | null; rh: number; selecionada: string | null; editavel: boolean;
  /** A largura que a coluna do perfil tem na página (361). */
  largura: number;
  geneses: { sigla: string; nome: string; fora_da_norma?: boolean }[];
  aoEscolher: (id: string) => void;
  /** 361: a gênese escolhida no próprio perfil, na coluna dela. */
  aoMudarGenese: (id: string, sigla: string | null) => void;
  /** 361: o «×» da descrição. */
  aoExcluir: (id: string) => void;
  aoDividir: (prof: number) => void;
  aoMudarBase: (id: string, base: number) => void;
  /** 366: a base da última camada é o fim do furo — digitada no perfil, muda a profundidade final. */
  aoMudarFim: (fim: number) => void;
  aoMudarNa: (prof: number) => void;
}) {
  const caixa = useRef<HTMLDivElement>(null);
  const [arrasto, setArrasto] = useState<Arrasto | null>(null);
  const [menuGenese, setMenuGenese] = useState<string | null>(null);
  /** 366: a profundidade da camada digitada (o id da camada e o texto do campo). */
  const [digitando, setDigitando] = useState<{ id: string; texto: string } | null>(null);
  const confirmarProfundidade = () => {
    if (!digitando) return;
    const i = camadas.findIndex((c) => c.id === digitando.id);
    const v = lerNumero(digitando.texto);
    setDigitando(null);
    if (i < 0 || v == null || !Number.isFinite(v)) return;
    const b = Math.round(v * 100) / 100;
    if (Math.abs(b - camadas[i].base) < 0.001) return;
    if (i === camadas.length - 1) aoMudarFim(b);
    else aoMudarBase(camadas[i].id, b);
  };
  const { COL, X, largura: LARGURA_DO_PERFIL } = colunas(disponivel);
  const G0 = X.grafico + 10;
  const GK = (COL.grafico - 20) / TETO_DO_GRAFICO;
  const ultimo = metros.length ? metros[metros.length - 1].profundidade + 1 : 1;
  const altura = (Math.ceil(Math.max(fim ?? 0, ultimo)) + 1) * rh;
  const y = (p: number) => p * rh;
  const { ini, fin } = pontosDoGrafico(metros);
  const linha = (ps: typeof ini) => ps.map((p) => `${(G0 + p.valor * GK).toFixed(1)},${y(p.prof).toFixed(1)}`).join(' ');

  const profDoPonteiro = (e: React.PointerEvent) => {
    const r = caixa.current!.getBoundingClientRect();
    return (e.clientY - r.top) / rh;
  };
  const mover = (e: React.PointerEvent) => {
    if (!arrasto) return;
    const p = profDoPonteiro(e);
    if (arrasto.tipo === 'base') setArrasto({ ...arrasto, prof: baseArrastada(p, arrasto.min, arrasto.max) });
    else setArrasto({ ...arrasto, prof: naArrastado(p, fim ?? p) });
  };
  const soltar = () => {
    if (!arrasto) return;
    if (arrasto.tipo === 'base') aoMudarBase(arrasto.id, arrasto.prof);
    else aoMudarNa(arrasto.prof);
    setArrasto(null);
  };

  const bases = camadas.map((c) => (arrasto?.tipo === 'base' && arrasto.id === c.id ? { ...c, base: arrasto.prof } : c));
  const topos = bases.map((_c, i) => (i === 0 ? 0 : bases[i - 1].base));
  const naProf = arrasto?.tipo === 'na' ? arrasto.prof : na;
  const inteiros: number[] = [];
  if (fim != null) for (let d = 1; d < fim; d += 1) if (!camadas.some((c) => Math.abs(c.base - d) < 0.001)) inteiros.push(d);
  const fenDe = (topo: number, base: number) =>
    fenomenos.filter((f) => f.de >= topo && f.de < base).map((f) => f.texto).filter(Boolean);
  const textoDe = (c: CamadaSpt, i: number) =>
    [c.descricao || 'Camada sem material — clique para descrever', ...fenDe(topos[i], c.base).map((t) => `— ${t}`)].join('\n');
  const arr = arrumarTextos(bases.map((c, i) => ({ topo: y(topos[i]) + 1, base: y(c.base) - 1, texto: textoDe(c, i) })), {
    largura: COL.descricao, fonteMax: 12, fonteMin: 9, entrelinha: 1.25, folga: 3, folgaX: 6, passo: 0.5,
  });
  const fundoDosTextos = arr.reduce((a, t) => Math.max(a, t.topo + t.altura), 0);
  const alturaTotal = Math.max(altura, fundoDosTextos + 4);

  return (
    <div className="spt-perfil" ref={caixa} style={{ width: LARGURA_DO_PERFIL, height: alturaTotal }}
      onPointerMove={mover} onPointerUp={soltar} onPointerLeave={soltar} data-arrastando={arrasto ? '' : undefined}>
      <svg width={LARGURA_DO_PERFIL} height={alturaTotal} role="img"
        aria-label="Perfil do furo: camadas, gráfico SPT, nível d'água e avanço">
        <defs dangerouslySetInnerHTML={{
          __html: [...new Set(camadas.map((c) => c.material).filter((m): m is string => !!m && !!HACHURAS[m]))]
            .map((m) => padraoDaHachura(m, `spt-h-${Object.keys(HACHURAS).indexOf(m)}`)).join(''),
        }} />
        {Array.from({ length: Math.ceil(altura / rh) }, (_, k) => (
          <line key={k} className="spt-grade" x1={0} x2={LARGURA_DO_PERFIL} y1={k * rh} y2={k * rh} />
        ))}
        {bases.map((c, i) => {
          const topo = topos[i];
          const h = Math.max(1, (c.base - topo) * rh);
          const id = c.material && HACHURAS[c.material] ? `spt-h-${Object.keys(HACHURAS).indexOf(c.material)}` : null;
          return (
            <g key={c.id}>
              <rect x={X.hachura} y={y(topo)} width={COL.hachura} height={h} fill={c.tinta ?? 'transparent'} />
              {id && <rect x={X.hachura} y={y(topo)} width={COL.hachura} height={h} fill={`url(#${id})`} className="spt-hachura" />}
              <rect x={X.hachura} y={y(topo)} width={COL.hachura} height={h}
                className={selecionada === c.id ? 'spt-camada-escolhida' : 'spt-camada'} />
              {!editavel && <text className="spt-sigla" x={X.genese + COL.genese / 2} y={y(topo) + h / 2 + 4}>{c.genese ?? ''}</text>}
              <line className="spt-base" x1={0} x2={X.grafico} y1={y(c.base)} y2={y(c.base)} />
              {!editavel && <text className="spt-cota mono" x={4} y={y(c.base) - 4}>{metro(c.base)}</text>}
            </g>
          );
        })}
        {[0, 10, 20, 30, 40, 50].map((v) => (
          <line key={v} className={v % 50 ? 'spt-eixo' : 'spt-eixo-borda'} x1={G0 + v * GK} x2={G0 + v * GK} y1={rh} y2={altura} />
        ))}
        <polyline className="spt-n-ini" points={linha(ini)} />
        <polyline className="spt-n-fin" points={linha(fin)} />
        {fin.map((p) => (
          <g key={p.prof}>
            <circle className="spt-ponto" cx={G0 + p.valor * GK} cy={y(p.prof)} r={2.4} />
            <text className={`spt-rotulo mono${p.alem ? ' spt-alem' : ''}`} x={G0 + p.valor * GK + (p.alem ? -4 : 5)}
              y={y(p.prof) - 3} textAnchor={p.alem ? 'end' : 'start'}>{p.alem ? `${p.rotulo} ▸` : p.rotulo}</text>
          </g>
        ))}
        {naProf != null && (
          <g className="spt-na">
            <line x1={X.grafico} x2={X.avanco} y1={y(naProf)} y2={y(naProf)} />
            <path d={`M${X.na + 14} ${y(naProf) - 10} h12 l-6 9 Z`} />
            <text x={X.na + 20} y={y(naProf) + 13} textAnchor="middle" className="mono">{metro(naProf)}</text>
          </g>
        )}
        {avanco.map((a, i) => (
          <g key={`${a.metodo}${i}`} className="spt-avanco">
            <path d={`M${X.avanco + 30} ${y(a.de) + 2} H${X.avanco + 24} V${y(a.ate) - 2} H${X.avanco + 30}`} />
            <text x={X.avanco + 14} y={y((a.de + a.ate) / 2) + 4} textAnchor="middle">{a.metodo}</text>
          </g>
        ))}
        {arr.map((t, i) => t.chamada && (
          <polyline key={`ch${bases[i].id}`} className="spt-chamada"
            points={`${X.descricao + 1},${((y(topos[i]) + y(bases[i].base)) / 2).toFixed(1)} ${X.descricao + 5},${(t.topo + Math.min(t.altura / 2, 10)).toFixed(1)}`} />
        ))}
        {fim != null && <line className="spt-fim" x1={0} x2={LARGURA_DO_PERFIL} y1={y(fim)} y2={y(fim)} />}
      </svg>

      {bases.map((c, i) => {
        const topo = topos[i];
        const fen = fenDe(topo, c.base);
        const t = arr[i];
        const rotulo = `a camada de ${metro(topo)} a ${metro(c.base)} m`;
        return (
          <div key={c.id} className={`spt-caixa-descricao${t.chamada ? ' spt-com-chamada' : ''}`}
            style={{ left: X.descricao, width: COL.descricao, top: t.topo, height: t.altura }}>
            <button type="button" className={`spt-descricao${selecionada === c.id ? ' spt-escolhida' : ''}`}
              style={{ fontSize: t.fonte }} onClick={() => aoEscolher(c.id)} aria-label={`Classificar ${rotulo}`}
              title={t.chamada || t.fonte < 12 ? textoDe(c, i).replace(/\n/g, ' ') : undefined}>
              {c.descricao || 'Camada sem material — clique para descrever'}
              {fen.map((x) => <span key={x} className="spt-fenomeno"> — {x}</span>)}
            </button>
            {editavel && camadas.length > 1 && (
              <button type="button" className="spt-excluir" aria-label={`Excluir ${rotulo}`} title="Excluir a camada"
                onClick={() => aoExcluir(c.id)}>×</button>
            )}
          </div>
        );
      })}

      {editavel && bases.map((c, i) => {
        const topo = topos[i];
        const g = geneses.find((x) => x.sigla === c.genese);
        return (
          <div key={`g${c.id}`} className="spt-genese" style={{ left: X.genese, width: COL.genese, top: y(topo) + 1, height: Math.max(2, (c.base - topo) * rh - 2) }}
            onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setMenuGenese(null); }}
            onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setMenuGenese(null); } }}>
            <button type="button" className={`spt-genese-botao${c.genese ? '' : ' spt-genese-vazia'}`}
              aria-haspopup="menu" aria-expanded={menuGenese === c.id}
              aria-label={`Gênese da camada de ${metro(topo)} a ${metro(c.base)} m: ${g ? g.nome : 'não escolhida'}`}
              title={g ? `${g.sigla} — ${g.nome}` : 'Escolher a gênese'}
              onClick={() => setMenuGenese(menuGenese === c.id ? null : c.id)}>{c.genese ?? '?'}</button>
            {menuGenese === c.id && (
              <div className="spt-menu-genese" role="menu" aria-label="Gênese (NBR 13441)">
                {geneses.map((x) => (
                  <button key={x.sigla} type="button" role="menuitemradio" aria-checked={x.sigla === c.genese}
                    autoFocus={x.sigla === (c.genese ?? geneses[0]?.sigla)}
                    onClick={() => { setMenuGenese(null); if (x.sigla !== c.genese) aoMudarGenese(c.id, x.sigla); }}>
                    <b>{x.sigla}</b> {x.nome}{x.fora_da_norma ? ' (fora da norma)' : ''}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {editavel && camadas.slice(0, -1).map((c, i) => (
        <div key={c.id} className="spt-limite" style={{ top: y(bases[i].base) - 6, width: X.descricao }}
          title="Arraste para mudar a base da camada"
          onPointerDown={(e) => {
            e.preventDefault();
            setArrasto({ tipo: 'base', id: c.id, min: i === 0 ? 0 : camadas[i - 1].base, max: camadas[i + 1].base, prof: c.base });
          }} />
      ))}

      {/* 366: a profundidade da transição (e a do término) se digita aqui, além de se arrastar a linha. */}
      {editavel && bases.map((c, i) => {
        const topo = i === 0 ? 0 : bases[i - 1].base;
        const rotulo = i === bases.length - 1
          ? `Profundidade do término do furo, ${metro(c.base)} m`
          : `Profundidade da transição da camada de ${metro(topo)} a ${metro(c.base)} m`;
        return digitando?.id === c.id ? (
          <input key={`cota${c.id}`} className="spt-cota-campo mono" style={{ top: y(c.base) - 20, width: COL.camada - 2 }}
            inputMode="decimal" aria-label={rotulo} autoFocus value={digitando.texto}
            onFocus={(e) => e.currentTarget.select()}
            onChange={(e) => setDigitando({ id: c.id, texto: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); confirmarProfundidade(); }
              if (e.key === 'Escape') { e.preventDefault(); setDigitando(null); }
            }}
            onBlur={confirmarProfundidade} />
        ) : (
          <button key={`cota${c.id}`} type="button" className="spt-cota-botao mono" style={{ top: y(c.base) - 20, width: COL.camada - 2 }}
            title="Clique para digitar a profundidade" aria-label={`${rotulo} — clique para digitar`}
            onClick={() => setDigitando({ id: c.id, texto: metro(c.base) })}>{metro(c.base)}</button>
        );
      })}

      {editavel && inteiros.map((d) => (
        <button key={d} type="button" className="spt-mais" style={{ top: y(d) - 10, left: X.hachura + COL.hachura / 2 - 10 }}
          aria-label={`Nova camada a ${metro(d)} m`} onClick={() => aoDividir(d)}>+</button>
      ))}

      {editavel && naProf != null && (
        <div className="spt-alca-na" style={{ top: y(naProf) - 14, left: X.na }}
          title="Arraste o nível d'água" onPointerDown={(e) => { e.preventDefault(); setArrasto({ tipo: 'na', prof: naProf }); }} />
      )}
    </div>
  );
}
